import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { toast } from 'react-toastify';

import { Job } from '../../types/job';
import { JobQuote } from '../../types/job';
import { Button } from '../common/Button';
import { formatDateLocalized } from '../../utils/dateUtils';
import {
  getQuotesCountLabel,
  getJobListingTypeLabel,
  isJobReopenable,
  isJobReviewable,
} from '../../utils/jobUtils';
import { getFreelancerSubtitle } from '../../utils/freelancerUtils';
import { useProfileStore } from '../../stores/profileStore';
import { useJobStore } from '../../stores/jobStore';
import JobListingType from '../jobs/JobListingType';
import JobPublishedDate from '../jobs/JobPublishedDate';

import BidderDetailsModal from '../quotes/BidderDetailsModal';
import JobFreelancerCard from '../jobs/JobFreelancerCard';
import PublishJobModal from '../jobs/PublishJobModal';
import EditPriorityModal from '../jobs/EditPriorityModal';
import GenericInformationModal from '../common/GenericInformationModal';
import EditJobModal from '../jobs/EditJobModal';
import FullBlockingModal from '../common/FullBlockingModal';

const getErrorMessage = (err: unknown): string => {
  const message = (err as { message?: unknown })?.message;
  return typeof message === 'string' ? message : String(err);
};

type Props = {
  job: Job;
  onClose: () => void;
  onEdit?: (jobId: string) => void;
  onChangePriority?: (jobId: string) => void;
  onPublish?: (jobId: string) => void;
};

const JobModal: React.FC<Props> = ({ job, onClose, onEdit, onChangePriority, onPublish }) => {
  const { t, i18n } = useTranslation(['jobs', 'common']);

  const { profile } = useProfileStore();
  const { openJob, pauseJob, publishJob, cancelJob, startReviewingJob } = useJobStore();

  const [showOfferModal, setShowOfferModal] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState<JobQuote | undefined>(undefined);

  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showEditJobModal, setShowEditJobModal] = useState(false);
  const [showUpdatePriorityModal, setShowUpdatePriorityModal] = useState(false);

  const [showPublishJobConfirmation, setShowPublishJobConfirmation] = useState(false);
  const [showEditJobConfirmationModal, setShowEditJobConfirmationModal] = useState(false);
  const [showJobCancelConfirmationModal, setShowJobCancelConfirmationModal] = useState(false);

  const quotes = job.quotes ?? [];

  const isOwner = profile?.id === job.client.id;

  const jobListingTypeLabel = getJobListingTypeLabel(job, t);

  const isPublished =
    job.visibility === 'private' || (job.visibility === 'public' && !!job.published_at);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const handleOpenJob = async () => {
    try {
      await openJob(job.id);
      toast.success(t('reopen_job_success', { ns: 'jobs' }));
    } catch (e) {
      console.error(e);
      toast.error(t('reopen_job_error', { ns: 'jobs' }));
    }
  };

  const handlePauseJob = async () => {
    try {
      await pauseJob(job.id);
      toast.success(t('pause_job_success', { ns: 'jobs' }));
    } catch (e) {
      console.error(e);
      toast.error(t('pause_job_error', { ns: 'jobs' }));
    }
  };

  const handleStartReviewingJob = async () => {
    try {
      await startReviewingJob(job.id);
      toast.success(t('start_reviewing_success', { ns: 'jobs' }));
    } catch (e) {
      console.error(e);
      toast.error(t('start_reviewing_error', { ns: 'jobs' }));
    }
  };

  const handlePublishJob = async (
    listingTypeId: number,
    expiresAt?: string,
    selectionDate?: string,
    publicationRequestId?: string
  ) => {
    try {
      await publishJob(job.id, listingTypeId, expiresAt, selectionDate, publicationRequestId);
      setShowPublishModal(false);
      setShowUpdatePriorityModal(false);
      onClose();
      setShowEditJobConfirmationModal(true);
    } catch (error) {
      console.error('Publish error:', error);
      const message = getErrorMessage(error);
      toast.error(
        message.includes('JOB_ALREADY_PUBLISHED')
          ? t('publish.already_published_error', { ns: 'jobs' })
          : t('publish.error', { ns: 'jobs' })
      );
    }
  };

  const handleEditClick = () => {
    if (onEdit) onEdit(job.id);
    else setShowEditJobModal(true);
  };

  const handleChangePriorityClick = () => {
    if (onChangePriority) onChangePriority(job.id);
    else setShowUpdatePriorityModal(true);
  };

  const handlePublishClick = () => {
    if (onPublish) onPublish(job.id);
    else setShowPublishModal(true);
  };

  const handleCancelJob = async () => {
    try {
      await cancelJob(job.id);
      setShowJobCancelConfirmationModal(false);
    } catch (error) {
      console.error('Cancel error:', error);
      toast.error(t('cancel_job_error', { ns: 'jobs' }));
    }
  };

  return (
    <FullBlockingModal
      className="items-start bg-black/40 p-4 overflow-y-auto"
      innerClassName="w-full md:w-full max-w-5xl md:max-w-5xl rounded-xl shadow-xl mt-6 mb-12 relative p-0 md:p-0 max-h-[90vh] overflow-y-auto"
      ariaLabelledBy="job-modal-title"
      onBackdropClick={onClose}
    >
      <div className="bg-white" onClick={e => e.stopPropagation()}>
        <div className="p-6 sticky top-0 bg-white z-10">
          <button
            type="button"
            aria-label={t('common:form_actions.close', { ns: 'common', defaultValue: 'Close' })}
            title={t('common:form_actions.close', { ns: 'common', defaultValue: 'Close' })}
            onClick={onClose}
            className="absolute right-0 top-0 inline-flex items-center justify-center rounded-full p-2
                       text-secondary hover:bg-gray-100 focus:outline-none
                       focus:ring-2 focus:ring-offset-2 focus:ring-gray-300"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>

          <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-start">
            <h2 id="job-modal-title" className="text-2xl font-bold text-secondary">
              {job.title}
            </h2>

            <div className="flex flex-col gap-2 md:flex-row md:items-end md:gap-8">
              {job.status === 'cancelled' && (
                <span className="text-white px-4 py-1 font-bold bg-primary w-max">
                  {t('cancelled', { ns: 'jobs' })}
                </span>
              )}

              {job.status === 'paused' && (
                <span className="text-white px-4 py-1 font-bold bg-[#FF564A] w-max">
                  {t('paused', { ns: 'jobs' })}
                </span>
              )}

              {jobListingTypeLabel && <JobListingType job={job} t={t} />}

              <JobPublishedDate job={job} t={t} language={i18n.language} className="my-2" />

              <div className="text-sm text-secondary bg-[#E1E9F0] px-4 py-1 rounded w-max">
                {getQuotesCountLabel(job as unknown as Job, t)}
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-300" />

        <div className="p-6">
          <h3 className="text-secondary text-xl font-bold md:my-4">
            {t('project_info', { ns: 'jobs', defaultValue: 'Project information' })}
          </h3>

          <div className="flex flex-col flex-wrap gap-x-8 gap-y-4 mb-8 text-sm text-secondary md:flex-row md:gap-y-1">
            <div className="flex flex-col">
              <span className="text-sm">{t('service_type', { ns: 'jobs' })} </span>
              <span className="font-bold text-[0.9375rem]">
                {getFreelancerSubtitle(
                  { service_category_slugs: (job.required_services ?? []).map(s => s.slug) },
                  t
                )}
              </span>
            </div>

            <div className="flex flex-col">
              <span className="text-sm">{t('form.location', { ns: 'jobs' })} </span>
              <span className="font-bold text-[0.9375rem]">{job.city?.name ?? '-'}</span>
            </div>

            <div className="flex flex-col">
              <span className="text-sm">{t('form.onsite', { ns: 'jobs' })} </span>
              <span className="font-bold text-[0.9375rem]">
                {job.on_site
                  ? t('form_fields.yes', { ns: 'common' })
                  : t('form_fields.no', { ns: 'common' })}
              </span>
            </div>

            <div className="flex flex-col">
              <span className="text-sm">{t('form.languages', { ns: 'jobs' })} </span>
              <span className="font-bold text-[0.9375rem] ">
                {(job.languages ?? []).map(l => t(`language.${l}`, { ns: 'common' })).join(', ')}
              </span>
            </div>

            <div className="flex flex-col">
              <span className="text-sm ">{t('budget', { ns: 'jobs' })}</span>
              <span className="font-bold text-[0.9375rem]">
                {job.budget_range?.code
                  ? t(`budget_ranges.${job.budget_range.code}`, { ns: 'common' })
                  : '-'}
              </span>
            </div>

            <div className="flex flex-col">
              <span className="text-sm ">{t('form.start_date', { ns: 'jobs' })} </span>
              <span className="font-bold text-[0.9375rem]">
                {job.start_date
                  ? formatDateLocalized(job.start_date, i18n.language, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })
                  : '-'}
              </span>
            </div>

            <div className="flex flex-col">
              <span className="text-sm ">{t('form.end_date', { ns: 'jobs' })} </span>
              <span className="font-bold text-[0.9375rem]">
                {job.end_date
                  ? formatDateLocalized(job.end_date, i18n.language, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })
                  : '-'}
              </span>
            </div>
          </div>

          {isOwner && job.status !== 'cancelled' && (
            <div
              className={`flex flex-col gap-4 md:items-center flex-wrap ${
                isPublished ? 'md:flex-row-reverse' : 'flex-col-reverse md:flex-row md:justify-end'
              }`}
            >
              {job.status === 'open' && (
                <Button
                  data-testid="edit-edit_job_button"
                  className="w-full md:w-auto md:px-8"
                  variant={isPublished ? 'primary' : 'transparent-with-border'}
                  onClick={handleEditClick}
                >
                  {t('edit_job_button', { ns: 'jobs', defaultValue: 'Edit my job offer' })}
                </Button>
              )}

              {job.visibility === 'public' &&
                (job.status === 'open' || job.status === 'paused') &&
                (isPublished ? (
                  <Button
                    className="w-full md:w-auto md:px-8"
                    variant="transparent-with-border"
                    onClick={handleChangePriorityClick}
                  >
                    {t('change_priority_button', {
                      ns: 'jobs',
                      defaultValue: 'Change display priority',
                    })}
                  </Button>
                ) : (
                  <Button
                    className="w-full md:w-auto md:px-8"
                    variant="primary"
                    onClick={handlePublishClick}
                  >
                    {t('publish_job_button', { ns: 'jobs', defaultValue: 'Publish this offer' })}
                  </Button>
                ))}

              {job.visibility === 'public' &&
                job.published_at &&
                (job.status === 'open' || job.status === 'paused') &&
                (job.status === 'paused' ? (
                  isJobReopenable(job) ? (
                    <Button
                      data-testid="edit-edit_job_button"
                      className="w-full md:w-auto md:px-8"
                      variant="transparent-with-border"
                      onClick={handleOpenJob}
                    >
                      {t('reopen_offer', { ns: 'jobs', defaultValue: 'Reopen this offer' })}
                    </Button>
                  ) : (
                    <p className="text-sm text-gray-500">
                      {t('reopen_unavailable_notice', {
                        ns: 'jobs',
                        defaultValue:
                          'This offer’s submission deadline passed while it was paused, so it can no longer be reopened directly. Contact support to bring it back.',
                      })}
                    </p>
                  )
                ) : (
                  <Button
                    className="w-full md:w-auto md:px-8"
                    variant="transparent-with-border"
                    onClick={handlePauseJob}
                  >
                    {t('pause_offer', { ns: 'jobs', defaultValue: 'Pause this offer' })}
                  </Button>
                ))}

              {job.visibility === 'public' &&
                job.published_at &&
                (job.status === 'open' || job.status === 'expired') &&
                (isJobReviewable(job) ? (
                  <Button
                    className="w-full md:w-auto md:px-8"
                    variant="transparent-with-border"
                    onClick={handleStartReviewingJob}
                  >
                    {t('start_reviewing_offer', {
                      ns: 'jobs',
                      defaultValue: 'Start reviewing quotes',
                    })}
                  </Button>
                ) : (
                  <p className="text-sm text-gray-500">
                    {t('no_quotes_to_review_notice', {
                      ns: 'jobs',
                      defaultValue: 'No quotes have been submitted for this offer yet.',
                    })}
                  </p>
                ))}
            </div>
          )}
        </div>

        <div className="border-t border-gray-300" />

        {/* Bidders */}
        {quotes.length > 0 && (
          <div className="p-6">
            <div className="text-secondary text-[1.25rem] font-bold mb-4">
              {t('bidders', { ns: 'jobs', defaultValue: 'Bidders' })}
            </div>

            <div className="flex flex-col gap-2">
              {quotes.map(quote => (
                <JobFreelancerCard
                  key={quote.id}
                  quote={quote}
                  onClick={() => {
                    setSelectedQuote(quote);
                    setShowOfferModal(true);
                  }}
                />
              ))}
            </div>
          </div>
        )}

        <div className="p-4 border-t border-gray-300 flex justify-end sticky bottom-0 bg-white z-10">
          <Button variant="transparent-with-border" onClick={onClose}>
            {t('common:form_actions.close', { ns: 'common', defaultValue: 'Close' })}
          </Button>
        </div>
      </div>

      {showOfferModal && selectedQuote && (
        <BidderDetailsModal
          onClose={() => setShowOfferModal(false)}
          job={job}
          quote={selectedQuote}
        />
      )}

      {showPublishModal && (
        <PublishJobModal
          job={job}
          onClose={() => {
            onClose();
            setShowPublishModal(false);
          }}
          onEdit={() => {
            setShowEditJobModal(true);
            setShowPublishModal(false);
          }}
          onPublish={handlePublishJob}
        />
      )}

      {showEditJobModal && (
        <EditJobModal
          job={job}
          onClose={() => setShowEditJobModal(false)}
          onJobUpdated={() => {
            setShowEditJobModal(false);
            setShowEditJobConfirmationModal(true);
          }}
          onCancelChange={() => {
            setShowEditJobModal(false);
            setShowJobCancelConfirmationModal(true);
          }}
        />
      )}

      {showUpdatePriorityModal && (
        <EditPriorityModal
          listingType={job.listing_type}
          onClose={() => setShowUpdatePriorityModal(false)}
          onUpdate={handlePublishJob}
        />
      )}

      {showPublishJobConfirmation && (
        <GenericInformationModal
          title={t('job_published', { ns: 'jobs' })}
          message={
            <Trans
              i18nKey="job_hint"
              ns="jobs"
              values={{ name: job.title }}
              components={{ strong: <strong /> }}
            />
          }
          onClose={() => setShowPublishJobConfirmation(false)}
          closeButtonLabel={t('back_to_jobs')}
        />
      )}

      {showEditJobConfirmationModal && (
        <GenericInformationModal
          title={t('job_updated', { ns: 'jobs' })}
          message={
            <Trans
              i18nKey="job_hint"
              ns="jobs"
              values={{ name: job.title }}
              components={{ strong: <strong /> }}
            />
          }
          onClose={() => setShowEditJobConfirmationModal(false)}
          closeButtonLabel={t('back_to_jobs')}
        />
      )}

      {showJobCancelConfirmationModal && (
        <GenericInformationModal
          title={t('cancel_job', { ns: 'jobs' })}
          message={
            <Trans
              i18nKey="cancel_job_hint"
              ns="jobs"
              values={{ name: job.title }}
              components={{ strong: <strong /> }}
            />
          }
          onClose={() => setShowJobCancelConfirmationModal(false)}
          onConfirm={handleCancelJob}
          closeButtonLabel={t('cancel_changes')}
          confirmButtonLabel={t('cancel_and_confirm')}
        />
      )}
    </FullBlockingModal>
  );
};

export default JobModal;
